/**
 * Canvas Workspace — the collaboration surface for Analytics Collaboration.
 *
 * Two card types live on the canvas:
 *   - **Query cards**: NL question → local pattern engine or Claude → SQL →
 *     DuckDB result → visualization. Editable SQL, switchable visuals.
 *   - **Note cards**: free-text scratchpad for ideas, observations, plans.
 *
 * Cards are draggable, resizable, and the whole canvas state persists in the
 * project's `draft` JSONB column via debounced auto-save.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../lib/api'
import { cachedTables, fetchTables } from '../lib/tablesCache'
import {
  nlQuery, runQuery, updateProject,
  type AnalyticsProject, type CanvasCard, type CanvasDraft,
  type NLQueryResponse, type PitchLayer, type QueryPerf, type VisualKind,
} from '../lib/projects'
import { useDraftAutosave } from '../lib/useDraftAutosave'
import PitchBoard, { cycleColor, nextLayerColor } from '../playground/PitchBoard'
import {
  DataTable, Visual, VISUALS, centreOrigin, guessMapping, orientRows, regionClip,
  type Column, type Mapping, type OrientationInfo, type Result,
} from '../playground/charts'

// ── Types ───────────────────────────────────────────────────────────────────

/** A table-list column; stack games' carry what they mean (the data
 *  dictionary's description). */
type TableInfo = { name: string; columns: (Column & { description?: string | null })[] }
type GameIdentity = {
  home_team_name: string | null
  away_team_name: string | null
  org_side: 'home' | 'away' | null
  tracking_home_index: 0 | 1 | null
}
type VideoSync = {
  game_file_id: number
  fps: number | null
  video_game_start_time: number
  video_halftime_start_time: number | null
  video_halftime_end_time: number | null
  video_url: string
  /** Layer-stack games: frame_idx -> video seconds, per period. Every
   *  period is listed; one without a video mark carries null. */
  periods?: { period_id: number; start_frame: number; end_frame: number
              video_start_s: number | null }[]
}
type TablesResponse = {
  tables: TableInfo[]
  missing: { name: string; detail: string }[]
  facts?: { identity?: GameIdentity } | null
  orientation?: OrientationInfo | null
  video?: VideoSync | null
  /** Layer-stack games: the club's stack team_id (their rows carry
   *  stack team ids, not the 0/1 tracking index). */
  our_team?: number | null
}

// ── Helpers ─────────────────────────────────────────────────────────────────

let _cardSeq = 0
function newCardId(): string {
  return `card-${Date.now()}-${++_cardSeq}`
}

/** Kinds a result can be pinned to the Pitch Board with. */
const SPATIAL_KINDS: VisualKind[] = [
  'pitch', 'heatmap', 'flow', 'pitchzones', 'shape', 'spacemap', 'passnet',
]

const VISUAL_KINDS: VisualKind[] = [
  'table', 'bar', 'line', 'scatter', 'pitch', 'heatmap', 'flow', 'histogram',
  'pitchzones', 'shape', 'spacemap', 'timeline', 'passnet', 'rose',
]

function queryCard(from: NLQueryResponse): CanvasCard {
  // The engine's suggestion wins when it recognized the question. Otherwise
  // the server's spatial contract decides: a result carrying pitch
  // coordinates opens ON the pitch (both ends of a mark -> flow, one point
  // -> pitch), and only a result with no coordinates starts as the table.
  const engineVisual = VISUAL_KINDS.includes(from.visual as VisualKind)
    ? from.visual as VisualKind
    : null
  const spatial = from.result?.spatial ?? null
  const visual: VisualKind = engineVisual
    ?? (spatial?.plottable?.includes('flow') ? 'flow'
      : spatial?.plottable?.includes('pitch') ? 'pitch'
        : 'table')
  const mapping = from.result
    ? guessMapping(visual, from.result.columns as Column[], spatial)
    : {}
  return {
    id: newCardId(),
    kind: 'query',
    question: from.question,
    sql: from.sql,
    explanation: from.explanation,
    source: from.source ?? '',
    result: from.result,
    error: from.error ?? null,
    visual,
    mapping: mapping as Record<string, string | undefined>,
    note: '',
    region: from.region ?? '',
    perf: from.perf ?? null,
    suggestions: from.suggestions ?? [],
    position: { x: 20 + (_cardSeq % 4) * 30, y: 20 + (_cardSeq % 4) * 30 },
    size: { w: 620, h: 460 },
  }
}

function noteCard(): CanvasCard {
  return {
    id: newCardId(),
    kind: 'note',
    question: '',
    sql: '',
    explanation: '',
    source: '',
    result: null,
    error: null,
    visual: 'table',
    mapping: {},
    note: '',
    region: '',
    perf: null,
    suggestions: [],
    position: { x: 20 + (_cardSeq % 4) * 30, y: 20 + (_cardSeq % 4) * 30 },
    size: { w: 360, h: 240 },
  }
}

/** A blank SQL card: the analyst writes the query by hand. It opens in
 *  edit mode (empty sql) and runs through the same read-only playground
 *  endpoint every generated card uses — no new server surface. */
function blankQueryCard(): CanvasCard {
  return {
    id: newCardId(),
    kind: 'query',
    question: 'Blank query',
    sql: '',
    explanation: '',
    source: 'manual',
    result: null,
    error: null,
    visual: 'table',
    mapping: {},
    note: '',
    region: '',
    perf: null,
    suggestions: [],
    position: { x: 20 + (_cardSeq % 4) * 30, y: 20 + (_cardSeq % 4) * 30 },
    size: { w: 620, h: 460 },
  }
}

// ── Canvas Workspace ────────────────────────────────────────────────────────

export default function CanvasWorkspace({ project, orgId, gameCode, readOnly = false, addCardRef, toggleTablesRef, onSeekVideo, onDraftChange }: {
  project: AnalyticsProject
  orgId: number
  gameCode: string
  /** Someone else's shared project: everything works locally (ask, pin,
   *  rearrange) but nothing writes back to the owner's draft. */
  readOnly?: boolean
  /** Ref for external callers (AI chat) to create a card with known SQL. */
  addCardRef?: React.MutableRefObject<((question: string, sql: string, visual?: string, explanation?: string) => void) | null>
  /** Ref for parent to toggle the table browser sidebar. */
  toggleTablesRef?: React.MutableRefObject<(() => void) | null>
  /** Clip Finder: seek the match viewer's video to a frame. */
  onSeekVideo?: (frame: number) => void
  onDraftChange: (draft: CanvasDraft) => void
}) {
  const [cards, setCards] = useState<CanvasCard[]>(() => {
    const draft = project.draft as CanvasDraft | null
    return draft?.cards ?? []
  })
  const [layers, setLayers] = useState<PitchLayer[]>(() => {
    const draft = project.draft as CanvasDraft | null
    return draft?.pitchLayers ?? []
  })
  const [tables, setTables] = useState<TableInfo[]>([])
  const [identity, setIdentity] = useState<GameIdentity | null>(null)
  const [orientation, setOrientation] = useState<OrientationInfo | null>(null)
  const [viewing, setViewing] = useState<'ours' | 'theirs'>('ours')
  // Collapsed by default: the board is a scratch surface for a subset of
  // sessions, and open it costs every other session the vertical space
  // between the query bar and the first result card. Its header strip
  // stays visible, so expanding is one click on the same chevron.
  const [boardOpen, setBoardOpen] = useState(false)
  // Floating board: popped out of the page flow into a fixed panel that
  // stays put while everything scrolls, dragged by its header, and
  // re-attachable to its slot under the query bar (Ben, 2026-09-03).
  const [boardFloat, setBoardFloat] = useState(false)
  const [boardPos, setBoardPos] = useState({ x: 96, y: 96 })
  const boardDrag = useRef<{ dx: number; dy: number } | null>(null)
  const onBoardDragStart = useCallback((e: React.PointerEvent) => {
    if (!boardFloat) return
    if ((e.target as HTMLElement).closest('button')) return // buttons stay buttons
    boardDrag.current = { dx: e.clientX - boardPos.x, dy: e.clientY - boardPos.y }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }, [boardFloat, boardPos])
  const onBoardDragMove = useCallback((e: React.PointerEvent) => {
    const d = boardDrag.current
    if (!d) return
    setBoardPos({
      x: Math.max(8, Math.min(window.innerWidth - 160, e.clientX - d.dx)),
      y: Math.max(8, Math.min(window.innerHeight - 48, e.clientY - d.dy)),
    })
  }, [])
  const onBoardDragEnd = useCallback(() => { boardDrag.current = null }, [])
  const [asking, setAsking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Open by default: the table browser is how you find out what you can
  // query, so a workspace that opens with it hidden asks every visit to
  // start with the same click. Toggled off from the same 'Tables' button.
  const [tablesOpen, setTablesOpen] = useState(true)
  const [openTable, setOpenTable] = useState<string | null>(null)
  const [videoSync, setVideoSync] = useState<VideoSync | null>(null)
  const [stackOurTeam, setStackOurTeam] = useState<number | null>(null)
  const { state: saveState, schedule: scheduleSave, retry: retrySave } =
    useDraftAutosave((draft: CanvasDraft) => updateProject(project.id, { draft }))

  // Load table metadata (and the game's team identity) for this game
  useEffect(() => {
    if (!orgId || !gameCode) return
    let alive = true
    const apply = (d: TablesResponse) => {
      setTables(d.tables ?? [])
      setIdentity(d.facts?.identity ?? null)
      setOrientation(d.orientation ?? null)
      setVideoSync(d.video ?? null)
      setStackOurTeam(d.our_team ?? null)
    }
    // The last copy at once, then the server's (lib/tablesCache).
    const seen = cachedTables<TablesResponse>(gameCode, orgId)
    if (seen) apply(seen)
    fetchTables<TablesResponse>(gameCode, orgId)
      .then((d) => { if (alive) apply(d) })
      .catch(() => {
        if (!alive || seen) return
        setTables([]); setIdentity(null); setOrientation(null)
        setVideoSync(null); setStackOurTeam(null)
      })
    return () => { alive = false }
  }, [orgId, gameCode])

  // The tracking team id that is "us" under the current lens — the same
  // resolution the server's perspective filter makes, kept client-side so
  // the pitch can obey the house rule (we attack right/up, they left/down).
  const ourTeam = (() => {
    if (stackOurTeam !== null) {
      // A stack game's two team ids are the orientation's keys.
      const other = Object.keys(orientation?.signs ?? {})
        .map(Number).find((t) => t !== stackOurTeam)
      return viewing === 'ours' ? stackOurTeam : (other ?? null)
    }
    if (identity?.org_side == null || identity.tracking_home_index == null) return null
    const club = identity.org_side === 'home'
      ? identity.tracking_home_index
      : 1 - identity.tracking_home_index
    return viewing === 'ours' ? club : 1 - club
  })()

  // Which club name is "us" under the current lens. Resolvable only when
  // game_facts recorded the identity; otherwise the toggle stays hidden and
  // perspective words go unfiltered rather than guessed.
  const identityKnown = (stackOurTeam !== null && identity != null)
    || (identity != null
        && identity.org_side != null && identity.tracking_home_index != null)
  const clubSideName = identity
    ? (identity.org_side === 'home' ? identity.home_team_name : identity.away_team_name)
    : null
  const otherSideName = identity
    ? (identity.org_side === 'home' ? identity.away_team_name : identity.home_team_name)
    : null
  const usLabel = (viewing === 'ours' ? clubSideName : otherSideName) ?? 'our team'
  const oppLabel = (viewing === 'ours' ? otherSideName : clubSideName) ?? 'opponent'

  // Auto-save draft when cards or pitch layers change (debounced). Refs feed
  // the half the caller did not pass, so either updater saves the whole draft.
  const cardsRef = useRef(cards)
  cardsRef.current = cards
  const layersRef = useRef(layers)
  layersRef.current = layers
  const saveDraft = useCallback((updatedCards: CanvasCard[], updatedLayers?: PitchLayer[]) => {
    const draft: CanvasDraft = {
      cards: updatedCards,
      pitchLayers: updatedLayers ?? layersRef.current,
    }
    onDraftChange(draft)
    // A viewer's local rearranging never writes into the owner's draft —
    // the server would 404 the PATCH anyway; this just doesn't send it.
    if (readOnly) return
    scheduleSave(draft)
  }, [scheduleSave, onDraftChange, readOnly])

  const updateCards = useCallback((fn: (prev: CanvasCard[]) => CanvasCard[]) => {
    setCards((prev) => {
      const next = fn(prev)
      saveDraft(next)
      return next
    })
  }, [saveDraft])

  const updateLayers = useCallback((fn: (prev: PitchLayer[]) => PitchLayer[]) => {
    setLayers((prev) => {
      const next = fn(prev)
      saveDraft(cardsRef.current, next)
      return next
    })
  }, [saveDraft])

  // ── NL query ────────────────────────────────────────────────────────────

  const handleAsk = useCallback(async (question: string) => {
    setAsking(true)
    setError(null)
    try {
      const resp = await nlQuery({
        question,
        game_code: gameCode,
        org_id: orgId,
        tables: tables.length ? tables : undefined,
        perspective: viewing,
      })
      const card = queryCard(resp)
      updateCards((prev) => [...prev, card])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Query failed')
    } finally {
      setAsking(false)
    }
  }, [gameCode, orgId, tables, viewing, updateCards])

  // ── Add card from AI chat (runs the SQL directly, skips NL translation) ──

  const addCardFromChat = useCallback(async (question: string, sql: string, visual?: string, explanation?: string) => {
    try {
      const result = await runQuery({ game_code: gameCode, org_id: orgId, sql })
      const card: CanvasCard = {
        id: newCardId(),
        kind: 'query',
        question,
        sql,
        explanation: explanation ?? '',
        source: 'llm',
        result,
        error: null,
        visual: (visual as VisualKind) ?? 'table',
        mapping: result ? guessMapping((visual as VisualKind) ?? 'table', result.columns as Column[], result.spatial ?? null) as Record<string, string | undefined> : {},
        note: '',
        region: '',
        perf: null,
        suggestions: [],
        position: { x: 20 + (_cardSeq % 4) * 30, y: 20 + (_cardSeq % 4) * 30 },
        size: { w: 620, h: 460 },
      }
      updateCards((prev) => [...prev, card])
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Query failed'
      const card: CanvasCard = {
        id: newCardId(),
        kind: 'query',
        question,
        sql,
        explanation: explanation ?? '',
        source: 'llm',
        result: null,
        error: msg,
        visual: 'table',
        mapping: {},
        note: '',
        region: '',
        perf: null,
        suggestions: [],
        position: { x: 20 + (_cardSeq % 4) * 30, y: 20 + (_cardSeq % 4) * 30 },
        size: { w: 620, h: 460 },
      }
      updateCards((prev) => [...prev, card])
    }
  }, [gameCode, orgId, updateCards])

  // Expose addCardFromChat to parent via ref
  useEffect(() => {
    if (addCardRef) addCardRef.current = addCardFromChat
    return () => { if (addCardRef) addCardRef.current = null }
  }, [addCardRef, addCardFromChat])

  // Expose table browser toggle to parent via ref
  useEffect(() => {
    if (toggleTablesRef) toggleTablesRef.current = () => setTablesOpen((v) => !v)
    return () => { if (toggleTablesRef) toggleTablesRef.current = null }
  }, [toggleTablesRef])

  // ── Note ────────────────────────────────────────────────────────────────

  const handleAddNote = useCallback(() => {
    updateCards((prev) => [...prev, noteCard()])
  }, [updateCards])

  const handleAddBlankQuery = useCallback(() => {
    updateCards((prev) => [...prev, blankQueryCard()])
  }, [updateCards])

  const handleNoteChange = useCallback((cardId: string, note: string) => {
    updateCards((prev) => prev.map((c) =>
      c.id === cardId ? { ...c, note } : c))
  }, [updateCards])

  // ── Card operations ─────────────────────────────────────────────────────

  // ── Pitch Board layers ──────────────────────────────────────────────────

  /** Pin a card's result onto the shared pitch as a layer (a snapshot —
   *  it survives the card being edited or removed). A faceted shape result
   *  becomes one layer per facet, so ball-channel shapes can be compared
   *  by toggling. */
  const handlePin = useCallback((cardId: string) => {
    const card = cardsRef.current.find((c) => c.id === cardId)
    if (!card?.result) return
    // The board draws every layer with DEFAULT_PITCH (meters, ours attack
    // up), so the snapshot is normalized ONCE, here at pin time: first the
    // house orientation (ours +x, theirs -x), then cm -> m where the server
    // declared centimeters. The layer then speaks the board's frame forever.
    const centred = centreOrigin(
      { columns: card.result.columns as Column[],
        rows: card.result.rows as (string | number | boolean | null)[][],
        spatial: card.result.spatial ?? null })
    const oriented = orientRows(centred, card.mapping as Mapping,
                                orientation, ourTeam)
    const baseRows = oriented ?? centred.rows
    const divisor = card.result.spatial?.frame.units_divisor ?? 1
    const coordCols = new Set(
      divisor !== 1 && card.result.spatial
        ? (['x', 'y', 'x2', 'y2'] as const)
          .map((r) => card.result!.spatial!.roles[r])
          .filter((n): n is string => !!n)
        : [])
    const pinRows = coordCols.size
      ? baseRows.map((r) => r.map((v, i) =>
        coordCols.has(card.result!.columns[i]!.name) && typeof v === 'number'
          ? v / divisor : v))
      : baseRows
    updateLayers((prev) => {
      let seq = 0
      const newId = () => `layer-${Date.now()}-${++seq}`
      const base = {
        kind: card.visual,
        columns: card.result!.columns,
        mapping: card.mapping,
        visible: true,
        region: card.region || undefined,
      }
      const fi = card.mapping.facet
        ? card.result!.columns.findIndex((c) => c.name === card.mapping.facet)
        : -1
      if (card.visual === 'shape' && fi >= 0) {
        const groups = new Map<string, unknown[][]>()
        for (const r of pinRows) {
          const k = String(r[fi])
          if (!groups.has(k)) groups.set(k, [])
          groups.get(k)!.push(r)
        }
        let acc = prev
        for (const [k, rows] of groups) {
          acc = [...acc, { ...base, id: newId(), rows,
            label: `${card.question} \u2014 ${k}`, color: nextLayerColor(acc) }]
        }
        return acc
      }
      return [...prev, { ...base, id: newId(), rows: pinRows,
        label: card.question, color: nextLayerColor(prev) }]
    })
  }, [updateLayers, orientation, ourTeam])

  const handleLayerToggle = useCallback((id: string) => {
    updateLayers((prev) => prev.map((l) =>
      l.id === id ? { ...l, visible: !l.visible } : l))
  }, [updateLayers])

  const handleLayerRemove = useCallback((id: string) => {
    updateLayers((prev) => prev.filter((l) => l.id !== id))
  }, [updateLayers])

  const handleLayerRecolor = useCallback((id: string) => {
    updateLayers((prev) => prev.map((l) =>
      l.id === id ? { ...l, color: cycleColor(l.color) } : l))
  }, [updateLayers])

  /** Edit-and-re-ask: the question changes, the card stays where it is.
   *  The whole answer is replaced — SQL, result, visual, suggestions —
   *  because a new question is a new translation, not a patched one. */
  const handleRename = useCallback((cardId: string, question: string) => {
    updateCards((prev) => prev.map((c) =>
      c.id === cardId ? { ...c, question } : c))
  }, [updateCards])

  const handleReask = useCallback(async (cardId: string, question: string) => {
    updateCards((prev) => prev.map((c) =>
      c.id === cardId ? { ...c, question, error: null, result: null } : c))
    try {
      const resp = await nlQuery({
        question,
        game_code: gameCode,
        org_id: orgId,
        tables: tables.length ? tables : undefined,
        perspective: viewing,
      })
      const fresh = queryCard(resp)
      updateCards((prev) => prev.map((c) =>
        c.id === cardId
          ? { ...fresh, id: c.id, position: c.position, size: c.size }
          : c))
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Query failed'
      updateCards((prev) => prev.map((c) =>
        c.id === cardId ? { ...c, error: msg } : c))
    }
  }, [gameCode, orgId, tables, viewing, updateCards])

  const handleRerun = useCallback(async (cardId: string, sql: string) => {
    updateCards((prev) => prev.map((c) =>
      c.id === cardId ? { ...c, error: null, result: null } : c))
    try {
      const result = await runQuery({ game_code: gameCode, org_id: orgId, sql })
      updateCards((prev) => prev.map((c) =>
        c.id === cardId ? { ...c, sql, result, error: null } : c))
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Query failed'
      updateCards((prev) => prev.map((c) =>
        c.id === cardId ? { ...c, sql, error: msg } : c))
    }
  }, [gameCode, orgId, updateCards])

  const handleVisualChange = useCallback((cardId: string, visual: VisualKind) => {
    updateCards((prev) => prev.map((c) => {
      if (c.id !== cardId) return c
      const mapping = c.result
        ? guessMapping(visual, c.result.columns as Column[], c.result.spatial ?? null)
        : {}
      return { ...c, visual, mapping: mapping as Record<string, string | undefined> }
    }))
  }, [updateCards])

  const handleMappingChange = useCallback((cardId: string, mapping: Record<string, string | undefined>) => {
    updateCards((prev) => prev.map((c) =>
      c.id === cardId ? { ...c, mapping } : c))
  }, [updateCards])

  /** Drag-to-reorder: move one card to another card's slot in the flow.
   *  The canvas is a wrapping flex flow, so order IS position — the old
   *  free drag updated an x/y nothing rendered, which is why dragging
   *  "did not seem to function". */
  const handleReorder = useCallback((fromId: string, toId: string) => {
    if (fromId === toId) return
    updateCards((prev) => {
      const from = prev.findIndex((c) => c.id === fromId)
      const to = prev.findIndex((c) => c.id === toId)
      if (from < 0 || to < 0) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved!)
      return next
    })
  }, [updateCards])

  const handleResize = useCallback((cardId: string, size: { w: number; h: number }) => {
    updateCards((prev) => prev.map((c) =>
      c.id === cardId ? { ...c, size } : c))
  }, [updateCards])

  const handleDelete = useCallback((cardId: string) => {
    updateCards((prev) => prev.filter((c) => c.id !== cardId))
  }, [updateCards])

  /** One-click copy of a cell — result, visual, mapping, note and all —
   *  dropped just below-right of the original so the pair reads as
   *  original-and-variant. No re-run: the clone starts from the same data
   *  and diverges from there (edit the SQL, switch the visual, re-ask). */
  const handleClone = useCallback((cardId: string) => {
    updateCards((prev) => {
      const idx = prev.findIndex((c) => c.id === cardId)
      const src = prev[idx]
      if (!src) return prev
      const copy: CanvasCard = {
        ...src,
        id: newCardId(),
        position: { x: src.position.x + 30, y: src.position.y + 30 },
        size: { ...src.size },
        mapping: { ...src.mapping },
      }
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)]
    })
  }, [updateCards])

  // ── Render ──────────────────────────────────────────────────────────────

  if (!gameCode || !orgId) {
    return (
      <div className="cv-empty">
        <p>Select a project with a game to start collaborating.</p>
      </div>
    )
  }

  return (
    <div className="cv-workspace">
      <div className="cv-toolbar">
        {identityKnown ? (
          <button
            className="cv-perspective"
            onClick={() => setViewing(viewing === 'ours' ? 'theirs' : 'ours')}
            title={`"We/our" is ${usLabel}; "opponent/they" is ${oppLabel}. Click to swap.`}
          >
            <span className="cv-perspective__label">Viewing as</span>
            <strong>{usLabel}</strong>
            <span className="cv-perspective__swap">&#x21C4;</span>
          </button>
        ) : (
          <Link
            className="cv-perspective cv-perspective--unset"
            to="/ops/layer-stack"
            title={'This game\u2019s team identity (which side is the club, which tracking index is home) is not recorded, so "we"/"opponent" questions run unfiltered. Set teams from the gear on Match Administration.'}
          >
            <span className="cv-perspective__label">Teams</span>
            <span className="muted">not set &mdash; fix in Match Administration</span>
          </Link>
        )}
        <button className="ac-btn" onClick={handleAddNote} title="Add a note card">
          + Note
        </button>
        <button className="ac-btn" onClick={handleAddBlankQuery}
                title="Add a blank SQL card — write any query by hand and run it">
          + SQL
        </button>
        {tables.length > 0 && (
          <button className="ac-btn" onClick={() => setTablesOpen((v) => !v)}
                  title="Toggle table browser sidebar">
            {tablesOpen ? 'Hide tables' : 'Tables'}
          </button>
        )}
      </div>
      {error && <p className="lab-error">{error}</p>}
      {saveState === 'failed' && (
        <p className="lab-error" role="alert">
          Your latest changes are not saved.{' '}
          <button type="button" onClick={retrySave}>Retry</button>
        </p>
      )}

      {/* The Pitch Board: under the query bar, above the results, full
          width. Sticky so it stays pinned as the cards scroll beneath it,
          collapsible to its header strip, and floatable — popped out into
          a fixed panel dragged by its header, re-attachable
          (Ben, 2026-09-03). */}
      <section
        className={`cv-board-strip${boardOpen ? '' : ' is-collapsed'}${boardFloat ? ' is-floating' : ''}`}
        style={boardFloat ? { left: boardPos.x, top: boardPos.y } : undefined}>
        <div className="cv-board-head"
             onPointerDown={onBoardDragStart}
             onPointerMove={onBoardDragMove}
             onPointerUp={onBoardDragEnd}>
          <strong>Pitch board</strong>
          {layers.length > 0 && (
            <span className="muted">{layers.length} layer{layers.length === 1 ? '' : 's'}</span>
          )}
          <button className="ac-btn-sm cv-board-toggle"
                  onClick={() => setBoardFloat((f) => !f)}
                  title={boardFloat ? 'Re-attach the board under the query bar' : 'Float the board (drag it by this header)'}>
            {boardFloat ? '\u2913 attach' : '\u2197 float'}
          </button>
          <button className="ac-btn-sm cv-board-toggle"
                  onClick={() => setBoardOpen((o) => !o)}
                  aria-expanded={boardOpen}
                  title={boardOpen ? 'Collapse the pitch board' : 'Expand the pitch board'}>
            {boardOpen ? '\u25be' : '\u25b8'}
          </button>
        </div>
        {boardOpen && (
          <PitchBoard layers={layers}
                      attack={identityKnown
                        ? { rightward: `${usLabel} attacking`, leftward: `${oppLabel} attacking` }
                        : undefined}
                      onToggle={handleLayerToggle}
                      onRemove={handleLayerRemove}
                      onRecolor={handleLayerRecolor} />
        )}
      </section>

      <div className="cv-canvas-area">
        {tablesOpen && tables.length > 0 && (
          <aside className="cv-table-browser" aria-label="Tables for this game">
            <div className="cv-table-browser__head">
              <h3>Tables</h3>
              <Link className="cv-table-browser__dict"
                    to="/insights-lab/data-dictionary"
                    title="What every table holds, and what each column means">
                Data dictionary
              </Link>
            </div>
            <ul className="pg-table-list">
              {tables.map((t) => (
                <li key={t.name}>
                  <div className="pg-table-row">
                    <button type="button" className="pg-link"
                            onClick={() => setOpenTable(openTable === t.name ? null : t.name)}
                            aria-expanded={openTable === t.name}>
                      {t.name}
                    </button>
                  </div>
                  {openTable === t.name && (
                    <ul className="pg-cols">
                      {t.columns.map((c) => (
                        <li key={c.name} title={c.description ?? undefined}>
                          <code>{c.name}</code> <span>{c.type}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </aside>
        )}
      <div className="cv-canvas">
        {cards.length === 0 && !asking && (
          <div className="cv-empty">
            <p>Use <strong>Ask AI</strong> to ask about the game data, or add a note.</p>
            <p className="muted">
              Try: &ldquo;Show me possession chains in the first half&rdquo;,
              &ldquo;Which players covered the most distance?&rdquo; or
              &ldquo;Show pressing intensity in 15 minute chunks&rdquo;
            </p>
          </div>
        )}
        {asking && cards.length === 0 && (
          <div className="cv-empty">
            <p className="muted">Thinking&hellip;</p>
          </div>
        )}
        {cards.map((card) => (
          card.kind === 'note' ? (
            <NoteCardView
              key={card.id}
              card={card}
              onNoteChange={handleNoteChange}
              onReorder={handleReorder}
              onResize={handleResize}
              onDelete={handleDelete}
              onClone={handleClone}
            />
          ) : (
            <QueryCardView
              key={card.id}
              card={card}
              orientation={orientation}
              ourTeam={ourTeam}
              usLabel={usLabel}
              oppLabel={oppLabel}
              identityKnown={identityKnown}
              onAsk={handleAsk}
              onRename={handleRename}
              onReask={handleReask}
              onPin={handlePin}
              onRerun={handleRerun}
              onSeekVideo={onSeekVideo}
              videoSync={videoSync}
              gameCode={gameCode}
              orgId={orgId}
              onVisualChange={handleVisualChange}
              onMappingChange={handleMappingChange}
              onReorder={handleReorder}
              onResize={handleResize}
              onDelete={handleDelete}
              onClone={handleClone}
            />
          )
        ))}
      </div>
      </div>
    </div>
  )
}

// ── NL Query Bar ────────────────────────────────────────────────────────────

// ── Shared drag-to-reorder / resize ─────────────────────────────────────────

/** The grip in a card's header: drag it onto another card to take that
 *  card's slot. HTML5 drag-and-drop, so the browser draws the ghost and a
 *  textarea/question stays selectable — the grip is the only drag source. */
function Grip({ id }: { id: string }) {
  return (
    <span className="cv-card__grip" draggable title="Drag to re-order"
          aria-label="Drag to re-order"
          onDragStart={(e) => {
            e.dataTransfer.setData('text/card-id', id)
            e.dataTransfer.effectAllowed = 'move'
          }}>
      &#x2847;&#x2847;
    </span>
  )
}

/** Drop-target props for a card's root: accept a dragged grip and hand the
 *  pair of ids to onReorder. */
function dropProps(cardId: string, onReorder: (fromId: string, toId: string) => void) {
  return {
    onDragOver: (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes('text/card-id')) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
      }
    },
    onDrop: (e: React.DragEvent) => {
      const fromId = e.dataTransfer.getData('text/card-id')
      if (!fromId) return
      e.preventDefault()
      onReorder(fromId, cardId)
    },
  }
}

function useResize(card: CanvasCard, onResize: (id: string, size: { w: number; h: number }) => void,
                   minW = 360, minH = 200) {
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null)

  const onResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    resizeRef.current = {
      startX: e.clientX, startY: e.clientY,
      origW: card.size.w, origH: card.size.h,
    }
    const onMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      onResize(card.id, {
        w: Math.max(minW, resizeRef.current.origW + (ev.clientX - resizeRef.current.startX)),
        h: Math.max(minH, resizeRef.current.origH + (ev.clientY - resizeRef.current.startY)),
      })
    }
    const onMouseUp = () => {
      resizeRef.current = null
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }
  return onResizeStart
}

// ── Note Card View ──────────────────────────────────────────────────────────

function NoteCardView({ card, onNoteChange, onReorder, onResize, onDelete, onClone }: {
  card: CanvasCard
  onNoteChange: (id: string, note: string) => void
  onReorder: (fromId: string, toId: string) => void
  onResize: (id: string, size: { w: number; h: number }) => void
  onDelete: (id: string) => void
  onClone: (id: string) => void
}) {
  const onResizeStart = useResize(card, onResize, 200, 120)

  return (
    <div
      className="cv-card cv-card--note"
      {...dropProps(card.id, onReorder)}
    >
      <div className="cv-card__head cv-card__head--note">
        <Grip id={card.id} />
        <span className="cv-card__question">Note</span>
        <button className="ac-btn-sm" onClick={() => onClone(card.id)}
                title="Clone this cell">&#x29C9;</button>
        <button className="ac-btn-sm" onClick={() => onDelete(card.id)} title="Remove">&times;</button>
      </div>
      <div className="cv-card__body">
        <textarea
          className="cv-note__editor"
          value={card.note}
          onChange={(e) => onNoteChange(card.id, e.target.value)}
          placeholder="Write your notes, ideas, observations&hellip;"
        />
      </div>
      <div className="cv-card__resize" onMouseDown={onResizeStart} />
    </div>
  )
}

// ── Query Card View ─────────────────────────────────────────────────────────

function QueryCardView({ card, orientation, ourTeam, usLabel, oppLabel, identityKnown, onRerun, onVisualChange, onMappingChange, onReorder, onResize, onDelete, onClone, onAsk, onRename, onReask, onPin, onSeekVideo, videoSync, gameCode, orgId }: {
  card: CanvasCard
  orientation: OrientationInfo | null
  ourTeam: number | null
  usLabel: string
  oppLabel: string
  identityKnown: boolean
  onAsk: (question: string) => void
  onRename: (id: string, question: string) => void
  onReask: (id: string, question: string) => void
  onPin: (id: string) => void
  onRerun: (id: string, sql: string) => void
  onSeekVideo?: (frame: number) => void
  videoSync?: VideoSync | null
  gameCode: string
  orgId: number
  onVisualChange: (id: string, visual: VisualKind) => void
  onMappingChange: (id: string, mapping: Record<string, string | undefined>) => void
  onReorder: (fromId: string, toId: string) => void
  onResize: (id: string, size: { w: number; h: number }) => void
  onDelete: (id: string) => void
  onClone: (id: string) => void
}) {
  // A blank card (added via "+ SQL") opens straight into the editor —
  // there is nothing to display until the analyst writes the query.
  const [editingSql, setEditingSql] = useState(card.sql === '')
  const [sqlDraft, setSqlDraft] = useState(card.sql)
  const [collapsed, setCollapsed] = useState(false)
  const [editingQ, setEditingQ] = useState(false)
  const [qDraft, setQDraft] = useState(card.question)

  const [clipFinder, setClipFinder] = useState(false)
  const [clipRow, setClipRow] = useState<number | null>(null)

  useEffect(() => { setSqlDraft(card.sql) }, [card.sql])
  useEffect(() => { setQDraft(card.question) }, [card.question])

  // Detect frame column for Clip Finder
  const FRAME_COL_NAMES = ['frame', 'start_frame', 'end_frame']
  const frameColIdx = card.result?.columns.findIndex(
    (c) => FRAME_COL_NAMES.includes(c.name)) ?? -1
  const hasFrameCol = frameColIdx >= 0 && !!onSeekVideo

  /** Rename only — no re-run. */
  const submitRename = () => {
    const question = qDraft.trim()
    setEditingQ(false)
    if (question && question !== card.question) onRename(card.id, question)
  }
  /** Rename AND re-run through the NL pipeline. */
  const submitReask = () => {
    const question = qDraft.trim()
    setEditingQ(false)
    if (question) onReask(card.id, question)
  }

  const onResizeStart = useResize(card, onResize)

  const result: Result | null = card.result
    ? { columns: card.result.columns as Column[],
        rows: card.result.rows as (string | number | boolean | null)[][],
        spatial: card.result.spatial ?? null }
    : null

  // House rule: our marks attack RIGHT, the opponent's LEFT. A draw-time
  // transform only — the Data table below stays as recorded. The captions
  // name the clubs, and appear only when the rows really were oriented; an
  // unoriented pitch makes no direction claim.
  const centred = result ? centreOrigin(result) : null
  const orientedRows = centred
    ? orientRows(centred, card.mapping as Mapping, orientation, ourTeam)
    : null
  const drawResult: Result | null = centred
    ? (orientedRows ? { ...centred, rows: orientedRows } : centred)
    : null
  const attack = orientedRows
    ? { rightward: `${usLabel} attacking`, leftward: `${oppLabel} attacking` }
    : undefined


  return (
    <div
      className="cv-card"
      {...dropProps(card.id, onReorder)}
    >
      <div className="cv-card__head">
        <Grip id={card.id} />
        <button className="cv-card__toggle" onClick={() => setCollapsed(!collapsed)}
                title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? '\u25B6' : '\u25BC'}
        </button>
        {editingQ ? (
          <>
            <input
              className="cv-card__question-input"
              value={qDraft}
              autoFocus
              onChange={(e) => setQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitRename()
                if (e.key === 'Escape') { setEditingQ(false); setQDraft(card.question) }
              }}
            />
            <button className="ac-btn-sm" onClick={submitRename}
                    title="Rename this card (cosmetic, keeps current data)">Save</button>
            <button className="ac-btn-sm" onClick={submitReask}
                    title="Re-ask this question through the AI and replace the data">Re-ask</button>
            <button className="ac-btn-sm"
                    onClick={() => { setEditingQ(false); setQDraft(card.question) }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="cv-card__question" title={card.question}>{card.question}</span>
            <button className="ac-btn-sm" onClick={() => setEditingQ(true)}
                    title="Rename this card">&#x270E;</button>
          </>
        )}
        {/* No source badge: which engine wrote a query (the model or the
            pattern matcher) is ours to know, not the reader's (Ben, 2026-10-26). */}
        {identityKnown && <span className="cv-card__perspective" title={`${usLabel} attacks right, ${oppLabel} attacks left`}>{usLabel} &#x2192;</span>}
        {card.result && SPATIAL_KINDS.includes(card.visual) && (
          <button className="ac-btn-sm" onClick={() => onPin(card.id)}
                  title="Pin this result onto the pitch board as a layer">
            + Pitch</button>
        )}
        {hasFrameCol && (
          <button className={`ac-btn-sm${clipFinder ? ' ac-btn-sm--active' : ''}`}
                  onClick={() => { setClipFinder((v) => !v); setClipRow(null) }}
                  title="Clip Finder — browse rows and watch each in the match viewer">
            Clips</button>
        )}
        <button className="ac-btn-sm" onClick={() => onClone(card.id)}
                title="Clone this cell — same data and visual, ready to vary">&#x29C9;</button>
        <button className="ac-btn-sm" onClick={() => onDelete(card.id)} title="Remove">&times;</button>
      </div>

      {!collapsed && (
        <div className="cv-card__body">
          {card.explanation && (
            <p className="cv-card__explanation">{card.explanation}</p>
          )}

          <div className="cv-card__sql-section">
            <div className="cv-card__sql-head">
              <span className="cv-card__sql-label">SQL</span>
              <button className="ac-btn-sm" onClick={() => setEditingSql(!editingSql)}>
                {editingSql ? 'Cancel' : 'Edit'}
              </button>
              {editingSql && (
                <button className="ac-btn-sm" disabled={!sqlDraft.trim()}
                        onClick={() => {
                          setEditingSql(false)
                          onRerun(card.id, sqlDraft)
                        }}>
                  Run
                </button>
              )}
            </div>
            {editingSql ? (
              <textarea
                className="cv-card__sql-editor"
                value={sqlDraft}
                placeholder="SELECT ..."
                onChange={(e) => setSqlDraft(e.target.value)}
                rows={4}
              />
            ) : (
              <pre className="cv-card__sql-display">{card.sql}</pre>
            )}
          </div>

          {card.error && <p className="lab-error">{card.error}</p>}

          {!card.result && !card.error && card.sql && (
            <p className="muted">Running query&hellip;</p>
          )}
          {!card.result && !card.error && !card.sql && (
            <p className="muted">Write a SELECT above and press Run.
              The query runs read-only against this game&rsquo;s tables.</p>
          )}

          {result && (
            <>
              <div className="cv-card__visual-bar">
                {VISUALS.map((v) => (
                  <button
                    key={v.kind}
                    className={`ac-btn-sm${card.visual === v.kind ? ' is-on' : ''}`}
                    onClick={() => onVisualChange(card.id, v.kind)}
                    title={v.hint}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              {card.visual !== 'table' && (
                <MappingControls
                  cardId={card.id}
                  visual={card.visual}
                  columns={result.columns}
                  mapping={card.mapping as Mapping}
                  onChange={onMappingChange}
                />
              )}
              <div className="cv-card__visual">
                {/* The divisor comes from the server's contract: the
                    parquets mix meters and centimeters, and a cm mark on a
                    meters pitch lands 100x off the canvas. */}
                <Visual
                  kind={card.visual}
                  result={drawResult ?? result}
                  mapping={card.mapping as Mapping}
                  pitch={{ lengthM: 105, widthM: 68, origin: 'center',
                           unitsDivisor: result.spatial?.frame.units_divisor ?? 1,
                           clip: card.region ? regionClip(card.region) : undefined,
                           attack }}
                />
              </div>
              {card.visual !== 'table' && (
                <details className="cv-card__data">
                  <summary className="muted">Data</summary>
                  <DataTable result={result} />
                </details>
              )}
              <div className="cv-card__meta">
                {card.result!.row_count.toLocaleString()} rows
                {card.result!.truncated ? ' (truncated)' : ''}
                {' \u00b7 '}
                {card.result!.elapsed_ms}ms
              </div>
              {(card.suggestions ?? []).length > 0 && (
                <div className="cv-card__suggestions">
                  <span className="cv-card__sugg-label">Related</span>
                  {(card.suggestions ?? []).map((s) => (
                    <button key={s.question} className="cv-sugg-chip"
                            title={s.question}
                            onClick={() => onAsk(s.question)}>
                      + {s.label}
                    </button>
                  ))}
                </div>
              )}
              {card.perf && <PerfPanel perf={card.perf} />}
              {clipFinder && hasFrameCol && (
                <ClipFinderPanel
                  result={card.result!}
                  frameColIdx={frameColIdx}
                  activeRow={clipRow}
                  videoSync={videoSync ?? undefined}
                  gameCode={gameCode}
                  orgId={orgId}
                  onSelectRow={(idx, frame) => {
                    setClipRow(idx)
                    onSeekVideo!(frame)
                  }}
                />
              )}
            </>
          )}
        </div>
      )}

      {!collapsed && (
        <div className="cv-card__resize" onMouseDown={onResizeStart} />
      )}
    </div>
  )
}

// ── Perf Panel ──────────────────────────────────────────────────────────────

function PerfPanel({ perf }: { perf: QueryPerf }) {
  const [open, setOpen] = useState(false)
  const hasWarnings = perf.warnings.length > 0

  return (
    <div className={`cv-perf${hasWarnings ? ' cv-perf--warn' : ''}`}>
      <button className="cv-perf__toggle" onClick={() => setOpen(!open)}>
        <span className="cv-perf__summary">
          {hasWarnings && <span className="cv-perf__badge">!</span>}
          {perf.total_ms}ms total
          {' \u00b7 '}
          translate {perf.translate_ms}ms
          {' \u00b7 '}
          query {perf.query_ms}ms
          {perf.meta_ms > 0 && <> {'\u00b7'} meta {perf.meta_ms}ms</>}
        </span>
        <span className="muted">{open ? 'hide' : 'details'}</span>
      </button>
      {open && (
        <div className="cv-perf__detail">
          <table className="cv-perf__table">
            <tbody>
              <tr><td>Total</td><td>{perf.total_ms}ms</td></tr>
              <tr><td>Table metadata</td><td>{perf.meta_ms}ms</td></tr>
              <tr><td>NL translation</td><td>{perf.translate_ms}ms</td></tr>
              <tr><td>Query execution</td><td>{perf.query_ms}ms</td></tr>
              {perf.row_count !== undefined && (
                <tr><td>Rows returned</td><td>{perf.row_count.toLocaleString()}</td></tr>
              )}
              {perf.tables_scanned && perf.tables_scanned.length > 0 && (
                <tr><td>Tables scanned</td><td>{perf.tables_scanned.join(', ')}</td></tr>
              )}
            </tbody>
          </table>
          {perf.warnings.length > 0 && (
            <div className="cv-perf__warnings">
              {perf.warnings.map((w, i) => (
                <p key={i} className="cv-perf__warning">{w}</p>
              ))}
            </div>
          )}
          {perf.plan && (
            <details className="cv-perf__plan-wrap">
              <summary className="muted">Query plan</summary>
              <pre className="cv-perf__plan">{perf.plan}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  )
}

// ── Mapping Controls ────────────────────────────────────────────────────────

function MappingControls({ cardId, visual, columns, mapping, onChange }: {
  cardId: string
  visual: VisualKind
  columns: Column[]
  mapping: Mapping
  onChange: (id: string, mapping: Record<string, string | undefined>) => void
}) {
  const colNames = columns.map((c) => c.name)
  const fields: { key: keyof Mapping; label: string }[] = []
  if (visual === 'histogram') {
    fields.push({ key: 'x', label: 'Value' })
  } else if (visual === 'pitchzones') {
    fields.push({ key: 'x', label: 'Region' })
    fields.push({ key: 'y', label: 'Value' })
  } else if (visual === 'shape') {
    fields.push({ key: 'x', label: 'X (attack axis)' })
    fields.push({ key: 'y', label: 'Y (lateral)' })
    fields.push({ key: 'label', label: 'Player' })
    fields.push({ key: 'facet', label: 'Split by' })
  } else if (visual === 'spacemap') {
    fields.push({ key: 'x', label: 'X' })
    fields.push({ key: 'y', label: 'Y' })
  } else if (visual === 'timeline') {
    fields.push({ key: 'x', label: 'Start (min)' })
    fields.push({ key: 'x2', label: 'End (min)' })
    fields.push({ key: 'label', label: 'Category' })
    fields.push({ key: 'series', label: 'Lane' })
  } else if (visual === 'passnet') {
    fields.push({ key: 'x', label: 'From X' })
    fields.push({ key: 'y', label: 'From Y' })
    fields.push({ key: 'x2', label: 'To X' })
    fields.push({ key: 'y2', label: 'To Y' })
    fields.push({ key: 'label', label: 'From player' })
    fields.push({ key: 'series', label: 'To player' })
    fields.push({ key: 'size', label: 'Count' })
  } else if (visual === 'rose') {
    fields.push({ key: 'x', label: 'Start X' })
    fields.push({ key: 'y', label: 'Start Y' })
    fields.push({ key: 'x2', label: 'End X' })
    fields.push({ key: 'y2', label: 'End Y' })
  } else if (visual === 'flow') {
    fields.push({ key: 'x', label: 'Start X' })
    fields.push({ key: 'y', label: 'Start Y' })
    fields.push({ key: 'x2', label: 'End X' })
    fields.push({ key: 'y2', label: 'End Y' })
    fields.push({ key: 'series', label: 'Series' })
  } else {
    fields.push({ key: 'x', label: 'X' })
    fields.push({ key: 'y', label: 'Y' })
    if (visual === 'line' || visual === 'scatter' || visual === 'pitch') {
      fields.push({ key: 'series', label: 'Series' })
    }
    if (visual === 'scatter' || visual === 'pitch') {
      fields.push({ key: 'size', label: 'Size' })
    }
  }

  return (
    <div className="cv-card__mapping">
      {fields.map((f) => (
        <label key={f.key}>
          {f.label}
          <select
            value={mapping[f.key] ?? ''}
            onChange={(e) => onChange(cardId, { ...mapping, [f.key]: e.target.value || undefined })}
          >
            <option value="">—</option>
            {colNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      ))}
      {visual === 'bar' && (
        <label title="Print each bar's value on the chart, not only in the hover tooltip">
          Values
          <input type="checkbox" checked={mapping.values === 'show'}
                 onChange={(e) => onChange(cardId,
                   { ...mapping, values: e.target.checked ? 'show' : undefined })} />
        </label>
      )}
    </div>
  )
}

// ── Clip Finder Panel ────────────────────────────────────────────────────

/** Column names that indicate context worth showing for each row. */
const CONTEXT_COLS: Record<string, string> = {
  phase: 'Phase', zone: 'Zone', start_zone: 'Start zone', end_zone: 'End zone',
  duration_seconds: 'Duration', pass_count: 'Passes', subtype: 'Type',
  counterpress: 'Counterpress', chain_outcome: 'Outcome', chain_xg: 'xG',
  box_entry: 'Box entry', confidence: 'Confidence', x: 'X', y: 'Y',
  team: 'Team', team_id: 'Team',
}

/** The period a stack frame_idx falls in, and its seconds into it. */
function stackPeriod(frame: number, sync: VideoSync) {
  const ps = sync.periods ?? []
  const i = ps.findIndex((p) => frame >= p.start_frame && frame <= p.end_frame)
  if (i < 0 || !sync.fps) return null
  return { i, into: (frame - ps[i]!.start_frame) / sync.fps }
}

/** Convert a game frame number to a video timestamp in seconds. */
export function frameToVideoSec(frame: number, sync: VideoSync): number | null {
  if (!sync.fps) return null
  if (sync.periods?.length) {
    // Layer stack: each period's first frame_idx is at its video mark.
    const at = stackPeriod(frame, sync)
    if (!at) return null
    const mark = sync.periods[at.i]!.video_start_s
    return mark == null ? null : mark + at.into
  }
  const start = sync.video_game_start_time || 0
  const htStart = sync.video_halftime_start_time
  const htEnd = sync.video_halftime_end_time
  const frameSec = frame / sync.fps
  // If halftime times exist and the frame is in the second half
  if (htStart != null && htEnd != null) {
    const htStartSec = htStart - start
    if (frameSec > htStartSec) {
      const gap = htEnd - htStart
      return start + frameSec + gap
    }
  }
  return start + frameSec
}

/** Convert a frame number to a match-time string like "23:15" or "67:02 (2H)".
 *  Subtracts the video-game-start offset and the halftime gap so the result
 *  is game minutes from kickoff. */
export function frameToMatchTime(frame: number, sync: VideoSync): string | null {
  if (!sync.fps) return null
  if (sync.periods?.length) {
    const at = stackPeriod(frame, sync)
    if (!at) return null
    // Match time: the earlier periods' lengths plus the time into this one.
    const before = sync.periods.slice(0, at.i).reduce(
      (s, p) => s + (p.end_frame - p.start_frame + 1) / sync.fps!, 0)
    const t = before + at.into
    const ts = `${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, '0')}`
    // Label by the period's own id, not its list index — a game whose
    // first half has no video mark still lists it, and extra time is real.
    const pid = sync.periods[at.i]!.period_id
    return pid === 2 ? `${ts} (2H)` : pid >= 3 ? `${ts} (ET)` : ts
  }
  const start = sync.video_game_start_time || 0
  let gameSec = (frame / sync.fps) - start
  let half = 1
  // If halftime times exist and this frame is after halftime
  const htStart = sync.video_halftime_start_time
  const htEnd = sync.video_halftime_end_time
  if (htStart != null && htEnd != null) {
    const htStartGame = htStart - start  // halftime in game-seconds
    if (gameSec > htStartGame) {
      gameSec -= (htEnd - htStart)  // subtract the break
      half = 2
    }
  }
  if (gameSec < 0) gameSec = 0
  const min = Math.floor(gameSec / 60)
  const sec = Math.floor(gameSec % 60)
  const ts = `${min}:${sec.toString().padStart(2, '0')}`
  return half === 2 ? `${ts} (2H)` : ts
}

function ClipFinderPanel({ result, frameColIdx, activeRow, videoSync, gameCode, orgId, onSelectRow }: {
  result: CanvasCard['result'] & object
  frameColIdx: number
  activeRow: number | null
  videoSync?: VideoSync
  gameCode: string
  orgId: number
  onSelectRow: (idx: number, frame: number) => void
}) {
  const activeRef = useRef<HTMLButtonElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [frameData, setFrameData] = useState<Record<string, {
    players: FramePlayer[]; ball: FrameBall | null
  }>>({})

  // Batch-fetch all frame positions when expanding
  useEffect(() => {
    if (!expanded) return
    const allFrames = (result.rows as unknown[][])
      .map((r) => r[frameColIdx])
      .filter((f): f is number => typeof f === 'number')
    if (!allFrames.length) return
    let alive = true
    api<{ frames: Record<string, { players: FramePlayer[]; ball: FrameBall | null }> }>(
      '/api/v1/playground/frame-positions', {
        method: 'POST',
        body: JSON.stringify({ game_code: gameCode, org_id: orgId, frames: allFrames }),
      })
      .then((d) => { if (alive) setFrameData(d.frames) })
      .catch(() => {})
    return () => { alive = false }
  }, [expanded, result.rows, frameColIdx, gameCode, orgId])

  // Keyboard nav: up/down arrows move through rows
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
      e.preventDefault()
      const rows = result.rows
      if (!rows.length) return
      const next = e.key === 'ArrowDown'
        ? Math.min((activeRow ?? -1) + 1, rows.length - 1)
        : Math.max((activeRow ?? 1) - 1, 0)
      const frame = rows[next]?.[frameColIdx]
      if (typeof frame === 'number') onSelectRow(next, frame)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [activeRow, result.rows, frameColIdx, onSelectRow])

  // Scroll active row into view
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [activeRow])

  // Decide which columns to show as context (skip frame col itself)
  const contextIdxs = result.columns
    .map((c: { name: string }, i: number) => ({ i, name: c.name, label: CONTEXT_COLS[c.name] }))
    .filter((c) => c.label && c.i !== frameColIdx)

  const fmtVal = (v: unknown): string => {
    if (v == null) return '\u2014'
    if (typeof v === 'number') return v % 1 === 0 ? String(v) : v.toFixed(2)
    if (typeof v === 'boolean') return v ? 'Yes' : 'No'
    return String(v)
  }

  const canEmbed = !!videoSync?.video_url && !!videoSync.fps

  // Find x/y columns for the pitch dot
  const xIdx = result.columns.findIndex((c: { name: string }) =>
    c.name === 'x' || c.name === 'x_att' || c.name === 'start_ball_x' || c.name === 'ball_x' || c.name === 'player_x')
  const yIdx = result.columns.findIndex((c: { name: string }) =>
    c.name === 'y' || c.name === 'start_ball_y' || c.name === 'ball_y' || c.name === 'player_y')

  return (
    <div className="cv-clip-finder">
      <div className="cv-clip-finder__head">
        <span className="cv-clip-finder__title">
          Clip Finder &mdash; {result.rows.length} rows
        </span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="ac-btn-sm" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Collapse all' : 'Expand all'}
          </button>
          <span className="muted">&uarr;&darr; arrows</span>
        </span>
      </div>
      <div className={`cv-clip-finder__list${expanded ? ' cv-clip-finder__list--expanded' : ''}`}>
        {(result.rows as unknown[][]).map((row, i) => {
          const frame = row[frameColIdx]
          if (typeof frame !== 'number') return null
          const isActive = i === activeRow
          const videoSec = videoSync ? frameToVideoSec(frame, videoSync) : null
          const px = xIdx >= 0 ? row[xIdx] : null
          const py = yIdx >= 0 ? row[yIdx] : null
          return (
            <div key={i} className={`cv-clip-finder__item${isActive ? ' cv-clip-finder__row--active' : ''}`}>
              <button
                ref={isActive ? activeRef : undefined}
                className="cv-clip-finder__row"
                onClick={() => onSelectRow(i, frame)}
              >
                <span className="cv-clip-finder__frame">
                  {videoSync ? frameToMatchTime(frame, videoSync) ?? `f${frame}` : `f${frame}`}
                </span>
                {contextIdxs.map((c) => (
                  <span key={c.i} className="cv-clip-finder__ctx">
                    <span className="cv-clip-finder__ctx-label">{c.label}</span>
                    {fmtVal(row[c.i])}
                  </span>
                ))}
              </button>
              {expanded && (
                <div className="cv-clip-finder__detail">
                  {canEmbed && videoSec != null ? (
                    <ClipVideo src={videoSync!.video_url} startSec={videoSec} />
                  ) : (
                    <div className="cv-clip-finder__video cv-clip-finder__video--loading">
                      No video available
                    </div>
                  )}
                  <FrameSnapshot
                    frame={frame}
                    matchTime={videoSync ? frameToMatchTime(frame, videoSync) : null}
                    players={frameData[String(frame)]?.players}
                    ball={frameData[String(frame)]?.ball}
                    eventX={typeof px === 'number' ? px : null}
                    eventY={typeof py === 'number' ? py : null}
                  />
                  <div className="cv-clip-finder__stats">
                    <div className="cv-clip-finder__stat cv-clip-finder__stat--time">
                      <span className="cv-clip-finder__stat-value">
                        {videoSync ? frameToMatchTime(frame, videoSync) ?? `f${frame}` : `f${frame}`}
                      </span>
                    </div>
                    {contextIdxs.map((c) => (
                      <div key={c.i} className="cv-clip-finder__stat">
                        <span className="cv-clip-finder__stat-label">{c.label}</span>
                        <span className="cv-clip-finder__stat-value">{fmtVal(row[c.i])}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Pitch SVG markings (shared) ──────────────────────────────────────────

const PITCH_LW = '0.4'
const PITCH_LC = 'rgba(255,255,255,0.7)'

function PitchMarkings() {
  const lw = PITCH_LW, lc = PITCH_LC
  return (
    <>
      <rect x="0" y="0" width="105" height="68" fill="#1a7a2e" rx="0.5" />
      <rect x="0" y="0" width="105" height="68" fill="none" stroke={lc} strokeWidth={lw} />
      <line x1="52.5" y1="0" x2="52.5" y2="68" stroke={lc} strokeWidth={lw} />
      <circle cx="52.5" cy="34" r="9.15" fill="none" stroke={lc} strokeWidth={lw} />
      <circle cx="52.5" cy="34" r="0.6" fill={lc} />
      <rect x="0" y="13.84" width="16.5" height="40.32" fill="none" stroke={lc} strokeWidth={lw} />
      <rect x="0" y="24.84" width="5.5" height="18.32" fill="none" stroke={lc} strokeWidth={lw} />
      <circle cx="11" cy="34" r="0.6" fill={lc} />
      <rect x="88.5" y="13.84" width="16.5" height="40.32" fill="none" stroke={lc} strokeWidth={lw} />
      <rect x="99.5" y="24.84" width="5.5" height="18.32" fill="none" stroke={lc} strokeWidth={lw} />
      <circle cx="94" cy="34" r="0.6" fill={lc} />
    </>
  )
}

type FramePlayer = { team: number; player: number; x: number; y: number }
type FrameBall = { x: number; y: number }

/** SVG pitch rendering pre-fetched player+ball positions for one frame.
 *  Coordinates in meters (centre-spot origin). */
function FrameSnapshot({ frame, matchTime, players, ball, eventX, eventY }: {
  frame: number
  matchTime?: string | null
  players?: FramePlayer[] | null
  ball?: FrameBall | null
  eventX?: number | null; eventY?: number | null
}) {
  const toSvg = (mx: number, my: number) => ({ cx: 52.5 + mx, cy: 34 - my })

  return (
    <svg className="cv-clip-finder__pitch" viewBox="-1 -1 107 70" aria-label={`Frame ${frame}`}>
      <PitchMarkings />
      {/* Players */}
      {players?.map((p, i) => {
        const { cx, cy } = toSvg(p.x, p.y)
        const color = p.team === 0 ? '#4a90d9' : '#e74c3c'
        return (
          <g key={i}>
            <circle cx={cx} cy={cy} r="1.6" fill={color} stroke="#fff" strokeWidth="0.3" />
            <text x={cx} y={cy + 0.5} textAnchor="middle" fontSize="1.8"
                  fill="#fff" fontWeight="bold" style={{ pointerEvents: 'none' }}>{p.player}</text>
          </g>
        )
      })}
      {/* Ball */}
      {ball && (() => {
        const { cx, cy } = toSvg(ball.x, ball.y)
        return <circle cx={cx} cy={cy} r="1" fill="#fff" stroke="#333" strokeWidth="0.3" />
      })()}
      {/* Event location highlight */}
      {typeof eventX === 'number' && typeof eventY === 'number' && (() => {
        const { cx, cy } = toSvg(eventX, eventY)
        return (
          <>
            <circle cx={cx} cy={cy} r="4" fill="rgba(255,200,0,0.25)" />
            <circle cx={cx} cy={cy} r="0.8" fill="#ffcc00" stroke="#333" strokeWidth="0.3" />
          </>
        )
      })()}
      {/* Loading */}
      {players == null && (
        <text x="52.5" y="36" textAnchor="middle" fontSize="4"
              fill="rgba(255,255,255,0.5)">Loading...</text>
      )}
      {/* Time + frame label */}
      <text x="2" y="4" fontSize="3" fill="rgba(255,255,255,0.8)">
        {matchTime ?? `f${frame}`}
      </text>
    </svg>
  )
}

/** Inline video player — resolves the /media/ redirect to a presigned R2 URL,
 *  then loads the <video> element and seeks to the target second. */
function ClipVideo({ src, startSec }: { src: string; startSec: number }) {
  const ref = useRef<HTMLVideoElement>(null)
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null)

  // The /media/game-file/<id>/video route normally returns a 302 to a
  // presigned R2 URL.  A <video src> hitting that through the Vite proxy
  // fails on the cross-origin redirect.  Fetch with ?url=1 to get the
  // presigned URL as JSON, then set it as the video src.
  useEffect(() => {
    let alive = true
    const sep = src.includes('?') ? '&' : '?'
    fetch(`${src}${sep}url=1`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((d: { url?: string } | null) => {
        if (alive && d?.url) setResolvedUrl(d.url)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [src])

  useEffect(() => {
    const video = ref.current
    if (!video || !resolvedUrl) return
    const onLoaded = () => { video.currentTime = startSec }
    if (video.readyState >= 1) {
      video.currentTime = startSec
    } else {
      video.addEventListener('loadedmetadata', onLoaded, { once: true })
    }
    return () => video.removeEventListener('loadedmetadata', onLoaded)
  }, [startSec, resolvedUrl])

  if (!resolvedUrl) {
    return <div className="cv-clip-finder__video cv-clip-finder__video--loading">Loading video&hellip;</div>
  }

  return (
    <video
      ref={ref}
      className="cv-clip-finder__video"
      src={`${resolvedUrl}#t=${Math.max(0, startSec - 3)}`}
      controls
      preload="metadata"
      muted
    />
  )
}
